import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useVariations(loanId: string) {
  return useQuery({
    queryKey: ['variations', loanId],
    queryFn: async () => {
      const { data } = await api.get(`/loans/${loanId}/variations`);
      return data;
    },
    enabled: !!loanId,
  });
}

export function useCreateVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      loanId: string;
      type: string;
      description?: string;
      effectiveDate: string;
      oldValue?: string;
      newValue?: string;
    }) => {
      const { loanId, ...payload } = data;
      const { data: result } = await api.post(`/loans/${loanId}/variations`, payload);
      return result;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['variations', loanId] });
    },
  });
}

export function useApproveVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, id }: { loanId: string; id: string }) => {
      const { data: result } = await api.post(`/loans/${loanId}/variations/${id}/approve`);
      return result;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['variations', loanId] });
    },
  });
}

export function useDeclineVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, id, reason }: { loanId: string; id: string; reason?: string }) => {
      const { data: result } = await api.post(`/loans/${loanId}/variations/${id}/decline`, {
        reason,
      });
      return result;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['variations', loanId] });
    },
  });
}

export function useExecuteVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, id }: { loanId: string; id: string }) => {
      const { data } = await api.post(`/loans/${loanId}/variations/${id}/execute`);
      return data;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['variations', loanId] });
    },
  });
}

export function useWithdrawVariation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ loanId, id }: { loanId: string; id: string }) => {
      const { data } = await api.post(`/loans/${loanId}/variations/${id}/withdraw`);
      return data;
    },
    onSuccess: (_, { loanId }) => {
      qc.invalidateQueries({ queryKey: ['variations', loanId] });
    },
  });
}
